import Vue from "Vue";
import { InputMessage, InputOption, KeyValue } from "../../../models/Input";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";

@Component
export default class InputDefaultForm extends Vue {
	
	private phoneKeys = [
		"NumpadAdd",
		"NumpadSubtract"
	];

	private controlKeys = [
		"Backspace",
		"ArrowLeft",
		"ArrowRight",
		"ArrowUp",
		"ArrowDown",
		"Delete",
		"Escape",
		"Home",
		"End",
		"ShiftLeft",
		"ShiftRight",
		"ControlLeft",
		"ControlRight",
		"Tab"
	];

	@Prop()
	public dataInputOption!: InputOption;

	@Prop()
	public success = false;

	@Prop()
	public isFocused = false;

	@Prop()
	public type;

	@Prop({ default: false })
	public hasIcon;

	@Prop()
	public listOptionDefault: KeyValue[] = [];

	private isDisabled = false;

	public mounted(): void {
		if (this.dataInputOption !== undefined) {
			this.dataInputOption.display = this.dataInputOption.vmodel;

			if (this.dataInputOption.icon === undefined) {
				this.dataInputOption.icon = "";
			}

			if (this.dataInputOption.label === undefined) {
				this.dataInputOption.label = "";
			}

			if (this.dataInputOption.vmodel === undefined) {
				this.dataInputOption.vmodel = "";
			}

			if (this.dataInputOption.disabled === undefined) {
				this.dataInputOption.disabled = false;
			}

			if (this.dataInputOption.message === undefined) {
				this.dataInputOption.message = "";
			}

			if (this.dataInputOption.required === undefined) {
				this.dataInputOption.required = false;
			}

			if (this.dataInputOption.counter === undefined) {
				this.dataInputOption.counter = false;
			}

			if (this.dataInputOption.max === undefined) {
				this.dataInputOption.max = (this.type === "textarea" ? 1000 : 50);
			}

			if (this.dataInputOption.sub === undefined) {
				this.dataInputOption.sub = true;
			}
		}
	}

	private wrongValue = false;
	public handlePaste(event: ClipboardEvent): void {
		const pastValue = event.clipboardData?.getData("text");

		if (pastValue != null) {
			this.dataInputOption.vmodel = pastValue;

			const validAction = (): void => {
				this.dataInputOption.error = false;

				this.dataInputOption.sub = false;
				this.dataInputOption.message = "";

				this.refreshVmodel();
				this.$emit("keydown");
				this.wrongValue = false;
			};

			if (this.type === "number" || this.type === "phone" || this.type === "auto") {
				if (this.ifKeyIsNumber({ key: pastValue })) {
					validAction();
				} else {
					this.wrongValue = true;
				}
			} else if (this.type === "decimal") {
				if (this.ifKeyIsDecimal({ key: pastValue })) {
					validAction();
				} else {
					this.wrongValue = true;
				}
			} else {
				validAction();
			}
		}
	}

	public keydown(e: any): void {
		this.dataInputOption.error = false;
		this.success = false;

		// Reset si un coller a été fait
		if (this.wrongValue) {
			this.dataInputOption.sub = false;
			this.dataInputOption.message = "";
			this.wrongValue = false;
		}

		this.refreshVmodel();

		// Controle l'action de ctrl/cmd pour ensuite ne pas effectuer d'actions au moment où l'utilisateur colle une valeur
		if (e.ctrlKey || e.cmdKey) {
			if (e.key === "v" || e.key === "V") {
				// Ne rien faire et laisser handlePaste gérer
			}
		} else {
			if (this.type === "number" || this.type === "auto") {
				if (!this.ifKeyIsNumber(e) && !this.ifKeyIsControl(e)) {
					e.preventDefault();
				} else {
					if (this.dataInputOption.maxValue === undefined) {
						this.$emit("keydown", e);
					} else {
						const futureNumber = parseFloat((e.target.value).toString() + e.key);
						this.checkMaxValue(futureNumber) ? this.$emit("keydown", e) : e.preventDefault();
					}
				}
			} else if (this.type === "phone") {
				if (!this.ifKeyIsNumber(e) && !this.ifKeyIsControl(e) && !this.ifKeyIsPhone(e)) {
					e.preventDefault();
				} else {
					this.$emit("keydown", e);
				}
			} else if (this.type === "decimal") {
				if (!this.ifKeyIsDecimal(e) && !this.ifKeyIsControl(e)) {
					e.preventDefault();
				} else {
					if (this.dataInputOption.maxValue === undefined) {
						this.$emit("keydown", e);
					} else {
						const futureNumber = parseFloat((e.target.value).toString() + e.key);
						this.checkMaxValue(futureNumber) ? this.$emit("keydown", e) : e.preventDefault();
					}
				}
			} else {
				this.$emit("keydown", e);
			}
		}
	}

	public keyup(e: any): void {
		if (this.type !== 'auto' && this.type !== 'autotext' && this.type !== 'date') {
			this.dataInputOption.vmodel = e.target.value;
		}

		this.$emit("keyup", e);
		this.refreshVmodel();
	}

	public focus(e: any): void {
		this.isFocused = true;

		if (this.type !== "select") {
			e.target.select();
		}

		this.$emit("focus", e);
		this.refreshVmodel();
	}

	public change(): void {
		// Notifie l'utilisateur qu'il y a une erreur avec l'action coller
		if (this.wrongValue) {
			this.dataInputOption.error = true;
			this.dataInputOption.sub = true;
			this.dataInputOption.message = "Le format de la donnée collée n'est pas acceptée.";
			this.refreshVmodel();
		} else {
			this.dataInputOption.error = false;
			this.success = true;
			this.dataInputOption.sub = false;
			this.dataInputOption.message = "";
			this.$emit("change");
			this.wrongValue = false;
		}
	}

	public blur(e: any): void {
		this.isFocused = false;
		this.$emit("blur", e);
		this.refreshVmodel();
	}

	public scroll(e: any): void {
		e.stopPropagation();
		e.preventDefault();
	}

	private checkHasValue(): boolean {
		return this.dataInputOption.vmodel !== "";
	}

	public isDefault(): boolean {
		return !this.success && !this.dataInputOption.error && !this.isFocused && !this.dataInputOption.disabled;
	}

	public isDefaultFocus(): boolean {
		return !this.success && !this.dataInputOption.error && this.isFocused && !this.dataInputOption.disabled;
	}

	public isSuccess(): boolean {
		return this.success && !this.dataInputOption.error && !this.isFocused && (!this.dataInputOption.disabled || this.dataInputOption.checkEvenIfDisabled == true);
	}

	public isSuccessFocus(): boolean {
		return this.success && !this.dataInputOption.error && this.isFocused && (!this.dataInputOption.disabled || this.dataInputOption.checkEvenIfDisabled == true);
	}

	public isError(): boolean {
		return !this.success && this.dataInputOption.error! && !this.isFocused && (!this.dataInputOption.disabled || this.dataInputOption.checkEvenIfDisabled == true);
	}

	public isErrorFocus(): boolean {
		return !this.success && this.dataInputOption.error! && this.isFocused && (!this.dataInputOption.disabled || this.dataInputOption.checkEvenIfDisabled == true);
	}

	protected refreshVmodel(): void {
		this.$emit("update:dataInputOption", this.dataInputOption);
		this.$emit("update:success", this.success);
		this.$emit("update:isFocused", this.isFocused);
	}

	public checkValidationDefault(): void {
		this.success = false;
		this.dataInputOption.error = false;
		this.dataInputOption.message = "";

		if (this.dataInputOption.required) {
			if (!this.checkHasValue()) {
				this.dataInputOption.error = true;
				this.dataInputOption.message = InputMessage.ChampRequis;
			} else {
				this.success = true;
			}
		} else {
			if (this.checkHasValue()) {
				this.success = true;
			}
		}

		this.$forceUpdate();
	}

	public ifKeyIsNumber(e: any): boolean {
		const numberRegex = new RegExp("^[0-9]+$", "g")
		return numberRegex.test(e.key);
	}

	public ifKeyIsControl(e: any): boolean {
		return ((this.controlKeys.filter(element => element === e.code).length === 1) ||
			((e.code === "KeyQ" || e.code === "KeyW" || e.code === "KeyY") && e.ctrlKey));
	}

	public ifKeyIsPhone(e: any): boolean {
		return ((this.phoneKeys.filter(element => element === e.code).length === 1) ||
			((e.code === "Digit5" || e.code === "Minus") && !e.shiftKey));
	}

	public ifKeyIsDecimal(e: any): boolean {
		const decimalRegex = new RegExp("^[0-9.,]+$", "g")
		return decimalRegex.test(e.key);
	}

	@Watch("dataInputOption.clear")
	private clear(): void {
		this.success = false;
		this.dataInputOption.vmodel = "";
		this.dataInputOption.display = "";
		this.dataInputOption.message = "";
		this.dataInputOption.error = false;

		this.$forceUpdate();
	}

	@Watch("dataInputOption.reset")
	private reset(): void {
		this.success = false;
		this.dataInputOption.message = "";
		this.dataInputOption.error = false;
	}

	@Watch("dataInputOption.vmodel")
	private watchVmodel(): void {
		if (this.type !== undefined && this.type !== "select" && this.type !== "auto" && this.type !== "autotext") {
			this.dataInputOption.display = this.dataInputOption.vmodel;
		}
	}

	@Watch("dataInputOption.disabled")
	private watchDisabled(): void {
		this.isDisabled = this.dataInputOption.disabled;
	}

	private checkMaxValue(num: number): boolean {
		return num <= this.dataInputOption.maxValue!;
	}
}



// WEBPACK FOOTER //
// ./src/shared/components/forms/default/InputDefaultForm.ts